import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import { createPortal } from "react-dom";
import {
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { formatCompactIsoCurrency } from "@/revenue-intelligence/lib/formatCurrency";
import { formatRiskPoints } from "@/revenue-intelligence/lib/risk-contracts";
import {
  PipelineHealthStageRow,
  PipelineRiskBubbleDeal,
  PipelineRiskBubbleChartPoint,
  PipelineRiskSeverityRow,
  buildPipelineRiskBubbleChartPoints,
  getPipelineRiskSeverity,
  pipelineRiskSeverityColors,
} from "./pipelineAnalysis";
import { getViewportAwareTooltipPosition } from "./tooltipPosition";
import { useStateOfSalesV2Forecast } from "./useForecastData";

type CompanyCurrency = ReturnType<typeof useStateOfSalesV2Forecast>["companyCurrency"];

function formatExpectedClose(value: Date | string | null | undefined) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
}

type BubbleTooltipState = {
  deal: PipelineRiskBubbleChartPoint;
  x: number;
  y: number;
};

function PipelineRiskBubbleTooltip({
  chart,
  companyCurrency,
  tooltip,
}: {
  chart: HTMLDivElement | null;
  companyCurrency: CompanyCurrency;
  tooltip: BubbleTooltipState | null;
}) {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipSize, setTooltipSize] = useState({
    height: 170,
    width: 224,
  });

  useLayoutEffect(() => {
    if (!tooltip || !tooltipRef.current) return;
    const bounds = tooltipRef.current.getBoundingClientRect();
    if (
      Math.abs(bounds.height - tooltipSize.height) < 0.5
      && Math.abs(bounds.width - tooltipSize.width) < 0.5
    ) {
      return;
    }
    setTooltipSize({
      height: bounds.height,
      width: bounds.width,
    });
  }, [tooltip, tooltipSize.height, tooltipSize.width]);

  if (!tooltip || !chart || typeof document === "undefined") return null;

  const chartBounds = chart.getBoundingClientRect();
  const position = getViewportAwareTooltipPosition({
    anchor: { x: tooltip.x, y: tooltip.y },
    chart: chartBounds,
    tooltip: tooltipSize,
    viewport: {
      height: window.innerHeight,
      width: window.innerWidth,
    },
  });

  return createPortal(
    <div
      ref={tooltipRef}
      role="tooltip"
      className="pointer-events-none fixed z-50 w-56 max-w-[calc(100vw-2rem)] rounded-[8px] border border-gray-200 bg-white p-3 text-xs shadow-sm"
      style={{
        left: position.x,
        top: position.y,
      }}
    >
      <p className="font-medium text-[#231942]">
        {tooltip.deal.name ?? "Unnamed deal"}
      </p>
      <dl className="mt-2 space-y-1.5 text-gray-600">
        <div className="flex items-center justify-between gap-5">
          <dt>Deal value</dt>
          <dd className="tabular-nums text-[#231942]">
            {formatCompactIsoCurrency(
              Number(tooltip.deal.value ?? 0),
              companyCurrency,
            )}
          </dd>
        </div>
        <div className="flex items-center justify-between gap-5">
          <dt>Pipeline Risk</dt>
          <dd className="tabular-nums text-[#231942]">
            {formatRiskPoints(
              Number(
                tooltip.deal.pipeline_risk_score
                  ?? tooltip.deal.weighted_risk_score
                  ?? 0,
              ),
            )}
          </dd>
        </div>
        <div className="flex items-center justify-between gap-5">
          <dt>Stage</dt>
          <dd className="max-w-40 text-right text-[#231942]">
            {tooltip.deal.stage_label ?? "—"}
          </dd>
        </div>
        <div className="flex items-center justify-between gap-5">
          <dt>Expected close</dt>
          <dd className="text-[#231942]">
            {formatExpectedClose(tooltip.deal.adjustedCloseDate)}
          </dd>
        </div>
        {tooltip.deal.urgency_bucket && (
          <div className="flex items-center justify-between gap-5">
            <dt>Urgency</dt>
            <dd className="capitalize text-[#231942]">
              {tooltip.deal.urgency_bucket}
            </dd>
          </div>
        )}
      </dl>
    </div>,
    document.body,
  );
}

export function PipelineRiskBubbleChart({
  companyCurrency,
  data,
  onSelect,
}: {
  companyCurrency: CompanyCurrency;
  data: PipelineRiskBubbleDeal[];
  onSelect: (deal: PipelineRiskBubbleDeal) => void;
}) {
  const chartData = buildPipelineRiskBubbleChartPoints(data);
  const chartRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<BubbleTooltipState | null>(null);
  const updateTooltip = (
    entry: { payload?: PipelineRiskBubbleChartPoint },
    event: { clientX: number; clientY: number },
  ) => {
    const deal = entry.payload ?? entry as PipelineRiskBubbleChartPoint;
    setTooltip({
      deal,
      x: event.clientX,
      y: event.clientY,
    });
  };

  return (
    <div ref={chartRef} className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ bottom: 28, left: 8, right: 16, top: 12 }}>
          <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" />
          <XAxis
            type="number"
            dataKey="plottedStageProbability"
            domain={[0, 100]}
            ticks={[0, 25, 50, 75, 100]}
            axisLine={false}
            tickLine={false}
            tick={{ fill: "#6B7280", fontSize: 11 }}
            unit="%"
            label={{
              fill: "#6B7280",
              fontSize: 11,
              offset: -18,
              position: "insideBottom",
              value: "Stage probability",
            }}
          />
          <YAxis
            type="number"
            dataKey="plottedPipelineRisk"
            domain={[0, 100]}
            ticks={[0, 25, 50, 75, 100]}
            axisLine={false}
            tickLine={false}
            tick={{ fill: "#6B7280", fontSize: 11 }}
            width={38}
            label={{
              angle: -90,
              fill: "#6B7280",
              fontSize: 11,
              position: "insideLeft",
              value: "Risk",
            }}
          />
          <ZAxis
            type="number"
            dataKey="bubbleValue"
            range={[100, 500]}
          />
          <Scatter
            data={chartData}
            isAnimationActive={false}
            className="cursor-pointer"
            onMouseEnter={(entry, _index, event) => {
              updateTooltip(entry, event);
            }}
            onMouseMove={(entry, _index, event) => {
              updateTooltip(entry, event);
            }}
            onMouseLeave={() => setTooltip(null)}
            onClick={(entry, _index, event) => {
              event?.stopPropagation?.();
              const selected = (entry as { payload?: PipelineRiskBubbleDeal }).payload
                ?? entry as PipelineRiskBubbleDeal;
              onSelect(selected);
            }}
          >
            {chartData.map(deal => (
              <Cell
                key={String(deal.id)}
                fill={pipelineRiskSeverityColors[
                  getPipelineRiskSeverity(Number(
                    deal.pipeline_risk_score ?? deal.weighted_risk_score ?? 0,
                  ))
                ]}
                fillOpacity={0.88}
                stroke={deal.urgency_bucket === "high" ? "#E0B1CB" : "#FFFFFF"}
                strokeWidth={deal.urgency_bucket === "high" ? 3 : 1.5}
              />
            ))}
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>
      <PipelineRiskBubbleTooltip
        chart={chartRef.current}
        companyCurrency={companyCurrency}
        tooltip={tooltip}
      />
    </div>
  );
}

export function PipelineRiskSeverityChart({
  companyCurrency,
  compact = false,
  data,
  onSelect,
}: {
  companyCurrency: CompanyCurrency;
  compact?: boolean;
  data: PipelineRiskSeverityRow[];
  onSelect?: (row: PipelineRiskSeverityRow) => void;
}) {
  return (
    <div className={compact ? "h-44" : "h-64"}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout="vertical"
          margin={{ bottom: 4, left: compact ? 4 : 18, right: 20, top: 4 }}
        >
          <CartesianGrid horizontal={false} stroke="#E5E7EB" strokeDasharray="3 3" />
          <XAxis
            type="number"
            axisLine={false}
            tickLine={false}
            tick={{ fill: "#6B7280", fontSize: 11 }}
            tickFormatter={(value) => formatCompactIsoCurrency(value, companyCurrency)}
          />
          <YAxis
            type="category"
            dataKey="label"
            axisLine={false}
            tickLine={false}
            tick={{ fill: "#4B5563", fontSize: 11 }}
            width={78}
          />
          <RechartsTooltip
            cursor={{ fill: "#F9FAFB" }}
            content={({ active, payload }) => {
              const row = payload?.[0]?.payload as PipelineRiskSeverityRow | undefined;
              if (!active || !row) return null;
              return (
                <div className="rounded-[8px] border border-gray-200 bg-white p-3 text-xs shadow-sm">
                  <p className="font-medium text-[#231942]">{row.label}</p>
                  <p className="mt-2 text-gray-600">
                    Exposed open-deal value: <span className="tabular-nums text-[#231942]">{formatCompactIsoCurrency(row.rawValue, companyCurrency)}</span>
                  </p>
                  <p className="mt-1 text-gray-600">
                    Exposed Forecast: <span className="tabular-nums text-[#231942]">{formatCompactIsoCurrency(row.forecastValue, companyCurrency)}</span>
                  </p>
                  <p className="mt-1 text-gray-500">{row.count} {row.count === 1 ? "deal" : "deals"}</p>
                </div>
              );
            }}
          />
          <Bar
            dataKey="rawValue"
            radius={[0, 5, 5, 0]}
            isAnimationActive={false}
            className={onSelect ? "cursor-pointer" : undefined}
            onClick={(entry) => {
              const selected = (entry as { payload?: PipelineRiskSeverityRow }).payload
                ?? entry as PipelineRiskSeverityRow;
              onSelect?.(selected);
            }}
          >
            {data.map(row => (
              <Cell key={row.key} fill={pipelineRiskSeverityColors[row.key]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function PipelineHealthStageChart({
  compact = false,
  data,
}: {
  compact?: boolean;
  data: PipelineHealthStageRow[];
}) {
  return (
    <div className={compact ? "h-48" : "h-72"}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout="vertical"
          margin={{ bottom: 4, left: compact ? 4 : 18, right: 24, top: 4 }}
        >
          <CartesianGrid horizontal={false} stroke="#E5E7EB" strokeDasharray="3 3" />
          <XAxis
            type="number"
            axisLine={false}
            tickLine={false}
            tick={{ fill: "#6B7280", fontSize: 11 }}
            tickFormatter={(value) => `${value.toFixed(0)}%`}
          />
          <YAxis
            type="category"
            dataKey="label"
            axisLine={false}
            tickLine={false}
            tick={{ fill: "#4B5563", fontSize: 11 }}
            width={compact ? 98 : 126}
          />
          <ReferenceLine
            x={100}
            stroke="#231942"
            strokeDasharray="4 4"
            strokeWidth={1.25}
          />
          <RechartsTooltip
            cursor={{ fill: "#F9FAFB" }}
            content={({ active, payload }) => {
              const row = payload?.[0]?.payload as PipelineHealthStageRow | undefined;
              if (!active || !row) return null;
              return (
                <div className="rounded-[8px] border border-gray-200 bg-white p-3 text-xs shadow-sm">
                  <p className="font-medium text-[#231942]">{row.label}</p>
                  <p className="mt-2 text-gray-600">
                    Coverage: <span className="tabular-nums text-[#231942]">{row.coveragePercentage.toFixed(1)}%</span>
                  </p>
                </div>
              );
            }}
          />
          <Bar dataKey="coveragePercentage" fill="#5E548E" radius={[0, 5, 5, 0]} isAnimationActive={false}>
            {data.map(row => (
              <Cell
                key={row.key}
                fill={row.coveragePercentage >= 100 ? "#1D819A" : "#9F86C0"}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
